<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16">
    <button
      class="cf-button cf-button--primary cf-button--lg cf-button--fill"
      type="button"
      :disabled="disabled || isLoadingLibraries"
      @click="openModalHandler('paymentInformation')"
    >
      <template v-if="primaryCtaText">
        {{ primaryCtaText }}
      </template>

      <template v-else>
        {{ total === 0 ? 'Next' : content.OrderSummary.metadata.buttonCta }}
      </template>
    </button>

    <!-- Payment Information Form Modal -->
    <modal name="paymentInformation">
      <template
        v-if="paymentModalTitle"
        #title
      >
        {{ paymentModalTitle }}
      </template>

      <!--
        the `session.isSignedIn` key forces the payment-information
        component to re-render when the value changes, so that the
        `mounted` or `created` methods in it's child components
        can re-run with the new signed in user data.
     -->
      <cf-payment-information
        :key="session.isSignedIn"
        :force-save-cards="forceSaveCards"
        :pay-cta-text="payCtaText"
        :payment-gateway="paymentGateway"
        :payment-modal-title="paymentModalTitle"
        :payment-total-title="paymentTotalTitle"
        :phone-is-required="phoneIsRequired"
        :email-input-state="emailInputState"
        :is-pre-auth="isPreAuth"
        :room-number="roomNumber"
        :gift-card-reload-payment-method="giftCardReloadPaymentMethod"
        :hidden-elements="hiddenElements"
        :sender-info="senderInfo"
        :tip="tip"
        :total="total"
        :tds-provider="tdsProvider"
        :loading-alt-payment-methods="isConfirmingOrderTotal"
        @checkout="checkout"
      >
        <template #top>
          <slot name="payment-modal-top" />

          <cf-title-separator :omit-title="true" />
        </template>
      </cf-payment-information>
    </modal>

    <!-- Confirm Location Modal (TODO: Move to global modals) -->
    <modal
      v-slot="{ data: { onConfirm, location } }"
      name="confirmPickupLocation"
      modal-style="compact"
      :display-header-title="false"
    >
      <cf-confirm-location
        :location="location"
        @confirm="onConfirm"
      />
    </modal>

    <!-- Order Total Changed Modal (TODO: Move to global modals) -->
    <modal
      name="totalChangedWarning"
      modal-style="compact"
      :display-header-title="false"
    >
      <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-y-16">
        <h2 class="cf-title cf-title--2">
          Order Total Changed
        </h2>

        <p class="cf-subtitle cf-subtitle--2">
          Your order total has changed. Please re-review your order before continuing.
        </p>

        <button
          class="cf-button cf-button--primary cf-button--lg cf-button--fill"
          type="button"
          @click="$closeModal('totalChangedWarning')"
        >
          Okay
        </button>
      </div>
    </modal>

    <modal
      v-slot="{ data }"
      name="chargeToRoom"
      modal-style="compact"
    >
      <cf-charge-to-room
        :hide-contact-info="hiddenElements.contactInfo"
        :pay-cta-text="payCtaText"
        :phone-is-required="phoneIsRequired"
        :email-input-state="emailInputState"
        :room-number-autofill="roomNumber"
        :show-total="!hiddenElements.total"
        :total="total"
        :hide-sms="data.hideSms"
        @checkout="checkout"
      />
    </modal>

    <modal
      name="chargeHouseAccount"
      modal-style="compact"
      :display-header-title="false"
    >
      <cf-charge-house-account
        :total="total"
        @checkout="checkout"
      />
    </modal>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import { setPaymentPageState } from '@/view/pages/order-ahead/payment-page/payment-page-state';

  import loadPaymentSDK from '@/helpers/load-payment-sdk';
  import getTdsProvider from '@/helpers/getTdsProvider';
  import logger from '@/services/logger';

  import CfConfirmLocation from './cf-confirm-location/index.vue';
  import CfPaymentInformation from './cf-payment-information/index.vue';

  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';
  import CfChargeToRoom from '@/view/components/globals/cf-charge-to-room/index.vue';
  import CfChargeHouseAccount from '@/view/components/globals/cf-charge-house-account/index.vue';



  export default {
    name: 'CfPaymentMethods',

    components: {
      CfConfirmLocation,
      CfPaymentInformation,
      CfChargeToRoom,
      CfChargeHouseAccount,
      CfTitleSeparator
    },

    inject: {
      $validator: {
        from: '$validator'
      },

      validatedForm: {
        from: 'validatedForm'
      }
    },

    props: {
      isPaymentPage: {
        type: Boolean,
        default: false
      },

      confirmPickupLocation: {
        type: Boolean,
        default: false
      },

      disabled: {
        type: Boolean,
        default: false
      },

      forceSaveCards: {
        type: Boolean,
        default: false
      },

      hiddenElements: {
        type: Object,
        default: () => ({}),
        validator(obj) {
          const validKeys = [
            'contactInfo',
            'giftCards',
            'roomCharge',
            'signIn',
            'smsReceipt',
            'tendr',
            'total',
            'altPaymentMethods'
          ];
          const invalidKey = Object.keys(obj).find(key => !validKeys.includes(key));
          if (invalidKey) logger.error(`'${invalidKey}' is not a valid hidden element. Must be one of: [${validKeys.join(', ')}]`);
          return !invalidKey;
        }
      },

      payCtaText: {
        type: String,
        default: 'Pay'
      },

      paymentInfoOpen: {
        type: Boolean,
        default: false
      },

      paymentGatewayOverride: {
        type: Object,
        default: null
      },

      paymentModalTitle: {
        type: String,
        default: null
      },

      paymentTotalTitle: {
        type: String,
        default: undefined
      },

      giftCardReloadPaymentMethod: {
        type: Object,
        default: null
      },

      primaryCtaText: {
        type: String,
        default: null
      },

      roomNumber: {
        type: String,
        default: null
      },

      reconfirmOrderTotal: {
        type: Boolean,
        default: false
      },

      isPreAuth: {
        type: Boolean,
        default: false
      },

      senderInfo: {
        type: Object,
        default() {
          return {
            fullName: null,
            // firstName: null,
            lastName: null
          };
        }
      },

      tip: {
        type: Number,
        default: 0
      },

      total: {
        type: Number,
        required: true
      },

      checkoutHandler: {
        type: Function,
        required: true
      }
    },

    data() {
      return {
        isPaymentInformationDisplayed: false,
        isLoadingLibraries: false,
        isConfirmingOrderTotal: false,
        tdsProvider: {}
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['orderDetails', 'session']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('socket', ['orderRoom']),

      paymentGateway() {
        return this.paymentGatewayOverride || this.selectedLocation.paymentGateway;
      },

      emailInputState() {
        const { emailHiddenOnCheckout, emailOptionalOnCheckout } = this.merchant.options;
        if (emailHiddenOnCheckout) return 'hidden';
        if (emailOptionalOnCheckout) return 'optional';
        return 'required';
      },

      phoneIsRequired() {
        const { fulfillmentType } = this.orderDetails;
        return fulfillmentType === 'delivery'
          || fulfillmentType === 'shipping'
          || (this.merchant.options.phoneRequiredOnCheckout && fulfillmentType !== 'dine-in');
      },

      isGroupOrderHost() {
        return this.orderDetails.orderType === 'group' && this.order.groupOrder?.you?.isHost && this.order.orderItemCount > 0;
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      async createdHandler() {
        this.isLoadingLibraries = true;

        await loadPaymentSDK(this.paymentGateway);

        this.tdsProvider = getTdsProvider(this.paymentGateway.providers);

        this.isLoadingLibraries = false;

        if (this.paymentInfoOpen) this.$openModal('paymentInformation');
      },

      checkout(info) {
        this.checkoutHandler(info);
      },

      close() {
        this.$closeModal('addGiftCard');
        this.$closeModal('chargeToRoom');
        this.$closeModal('chargeHouseAccount');
        this.$closeModal('missingContactInformation');
        this.$closeModal('paymentInformation');
      },

      async openModalHandler(modalName) {
        const { enabled, dollarTriggerAmount, triggerBeforeViewTicket } = this.merchant.payAtTable2FA;

        const requiresAuthBeforePayment = enabled && !triggerBeforeViewTicket;

        const meetsCheckoutTotalThreshold = this.orderRoom.you.total >= dollarTriggerAmount;

        const formIsValid = await this.validatedForm.validate();

        if (formIsValid) {
          if (this.confirmPickupLocation) {
            this.$openModal('confirmPickupLocation', {
              location: this.selectedLocation,
              onConfirm: () => {
                if (this.isGroupOrderHost && !this.order.groupOrder?.isLocked) {
                  this.openGroupOrderLockModal();
                }
                else if (this.isPaymentPage) {
                  this.goToPaymentPage();
                }
                else {
                  this.$openModal(modalName);
                }
              }
            });
          }
          else if (requiresAuthBeforePayment && meetsCheckoutTotalThreshold && !this.session.isSignedIn) {
            const passcodeModalPayload = {
              onSuccess: () => {
                this.$openModal(modalName);
              }
            };

            this.$openModal('requestTwoFactorAuthPasscodeModal', passcodeModalPayload);
          }
          else if (!this.isPaymentPage) {
            this.$openModal(modalName);
          }
          else if (this.isGroupOrderHost) {
            this.openGroupOrderLockModal();
          }
          else {
            this.goToPaymentPage();
          }
        }

        if (this.reconfirmOrderTotal) {
          try {
            this.isConfirmingOrderTotal = true;
            await this.$store.dispatch('orders/getOrderAtTable', {
              silent: true,
              confirmTotalBeforePayment: true
            });
          }
          finally {
            this.isConfirmingOrderTotal = false;
          }
        }
      },

      openModal(modalName) {
        this.$openModal(modalName, {}, { closeModal: 'confirmPickupLocation' });
      },

      openGroupOrderLockModal() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Lock Order',
          primaryText: 'By proceeding to checkout, your group\'s order will be locked - not allowing further changes. You can exit the checkout process to unlock your order.',
          onConfirm: () => this.confirmGroupOrderLock(),
          title: 'Lock group order?'
        });
      },

      async confirmGroupOrderLock() {
        try {
          await this.$store.dispatch('orders/groupOrderUpdateLock', { isLocked: true });

          this.goToPaymentPage();
        }
        catch (error) {
          errorHandlers.generic(error);
        }
      },

      goToPaymentPage() {
        setPaymentPageState({
          forceSavedCards: this.forceSaveCards,
          payCtaText: this.payCtaText,
          paymentGateway: this.paymentGateway,
          paymentModalTitle: this.paymentModalTitle,
          paymentTotalTitle: this.paymentTotalTitle,
          phoneIsRequired: this.phoneIsRequired,
          emailInputState: this.emailInputState,
          isPreAuth: this.isPreAuth,
          roomNumber: this.roomNumber,
          giftCardReloadPaymentMethod: this.giftCardReloadPaymentMethod,
          hiddenElements: this.hiddenElements,
          tip: this.tip,
          total: this.total,
          tdsProvider: this.tdsProvider,
          loadingAltPaymentMethods: this.isConfirmingOrderTotal,
          checkoutHandler: info => this.checkout(info)
        });

        this.$router.push({
          name: 'paymentPage',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      }
    }
  };
</script>
